import { RemoteResultCamera } from '../RemoteResultCamera';
import { RemoteResultOutcomeGate } from '../RemoteResultOutcomeGate';
import { useImageEvaluation } from '../../useImageEvaluation';
import Dialog from '../../../../../components/envago/Dialog/Dialog';
import React, { Reducer, useEffect, useReducer } from 'react';
import { RemoteResult, RemoteResultConfiguration } from '../../index.types';
import { ActionType, StateType } from './RemoteResultDialog.types';
import RemoteResultDialogHelper from './RemoteResultDialog.helper';

import RemoteResultHelper from '../../../../../helper/RemoteResultHelper';
import { useDispatch, useSelector } from 'react-redux';
import aiApi from '../../../../../api/ai.api';
import { loginActions } from '../../../../../redux/login';
import { applicationSelectors } from '../../../../../redux/application';
import { RemoteResultImageSource } from '../../remoteResultImageSource';

interface RemoteResultDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChange: (result: RemoteResult | undefined, imageBase64: string) => void;
    config: RemoteResultConfiguration;
    allowGallery?: boolean;
    aiEnabled: boolean;
}

export const remoteResultReducer: Reducer<StateType, ActionType> = (_state, action) => {
    switch (action.type) {
        case 'TAKE_IMAGE':
            return { stage: 'CAMERA' };
        case 'RESULT_RECEIVED':
            return {
                stage: 'CONFIRMATION',
                payload: action.payload,
            };
        case 'CLOSE':
            return { stage: 'IDLE' };
        default:
            return RemoteResultDialogHelper.assertNever(action);
    }
};

const RemoteResultDialog = ({ open, onChange, config, onOpenChange, allowGallery = false, aiEnabled }: RemoteResultDialogProps) => {
    const [state, dispatch] = useReducer(remoteResultReducer, { stage: 'IDLE' });
    const { evaluate } = useImageEvaluation(config);

    useEffect(() => {
        if (open) {
            dispatch({ type: 'TAKE_IMAGE' });
        } else {
            dispatch({ type: 'CLOSE' });
        }
    }, [open]);

    const handleClose = () => {
        dispatch({ type: 'CLOSE' });
        onOpenChange(false);
    };

    // Hands the result to the confirmation stage. Outcome resolution, crop enrichment and the
    // meter-number candidate match all happen elsewhere already — in RemoteResultOutcomeGate and
    // useImageEvaluation respectively — so this only moves the payload along.
    const handleResult = (result: RemoteResult | undefined, imageBase64: string, imageSource: RemoteResultImageSource) => {
        dispatch({
            type: 'RESULT_RECEIVED',
            payload: {
                result: result,
                imageBase64: imageBase64,
                imageSource: imageSource,
            },
        });
    };

    const handleConfirm = (result: RemoteResult | undefined, imageBase64: string) => {
        onChange(result, imageBase64);
    };

    const evaluateImage = async (imageBase64: string, imageSource: RemoteResultImageSource) => {
        if (!aiEnabled) {
            onChange(undefined, imageBase64);
            handleClose();
            return;
        }

        const { result, superseded } = await evaluate(imageBase64);

        // A newer image has taken over — this answer is stale and must not open a confirmation.
        if (superseded) {
            return;
        }

        handleResult(result, imageBase64, imageSource);
    };

    const handleReset = () => {
        dispatch({
            type: 'TAKE_IMAGE',
        });
    };

    if (!open || state.stage === 'IDLE') {
        return null;
    }

    return (
        <>
            <Dialog visible={state.stage === 'CAMERA'} onClose={handleClose} closable={true} title={''} fullscreen={true}>
                <RemoteResultCamera
                    config={config}
                    allowGallery={allowGallery}
                    aiEnabled={aiEnabled}
                    onGalleryImageSelected={(imageBase64) => evaluateImage(imageBase64, 'gallery')}
                    onRemoteResultReceived={(result, imageBase64) => {
                        handleResult(result, imageBase64, 'camera');
                    }}
                    onClose={() => {
                        handleClose();
                    }}
                />
            </Dialog>
            {state.stage === 'CONFIRMATION' && (
                <RemoteResultOutcomeGate
                    config={config}
                    result={state.payload.result}
                    imageBase64={state.payload.imageBase64}
                    imageSource={state.payload.imageSource}
                    onConfirm={(result, imageBase64) => {
                        handleConfirm(result, imageBase64);
                        handleClose();
                    }}
                    onRetakeImage={handleReset}
                    onCancel={handleClose}
                />
            )}
        </>
    );
};

export default RemoteResultDialog;
